import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  MailIcon,
  MessageSquareIcon,
} from "lucide-react";

import { Row } from "@/components/Row";
import { CallIcon } from "@/components/CallIcon";

import { cn } from "@/lib/utils";
import {
  CallDirection,
  EmailStatus,
  LastPing,
  PingType,
  QuoMessageStatus,
} from "@/types";

const messageStatusIconColor = (status: QuoMessageStatus): string => {
  switch (status) {
    case QuoMessageStatus.DELIVERED:
      return "text-green-600";
    case QuoMessageStatus.UNDELIVERED:
      return "text-red-500";
    case QuoMessageStatus.SENT:
    case QuoMessageStatus.QUEUED:
      return "text-slate-500";
    default:
      return "text-slate-500";
  }
};

const emailStatusIconColor = (status: EmailStatus): string => {
  switch (status) {
    case EmailStatus.SENT:
      return "text-green-600";
    case EmailStatus.FAILED:
      return "text-red-500";
    default:
      return "text-slate-500";
  }
};

export const PingIcons = ({
  lastPing,
  colorClass,
}: {
  lastPing: LastPing;
  /** Optional override for icon color (e.g. to match a freshness tint).
   *  When omitted, color is derived from call / message status. */
  colorClass?: string;
}) => {
  const isIncoming = lastPing.direction === CallDirection.INCOMING;

  /* Render */
  const render = () => {
    if (lastPing.type === PingType.CALL) {
      // Ping cells never had a disc behind human call icons — only AI calls
      // keep the gradient ring here.
      return (
        <CallIcon
          circleClassName={cn(
            "h-5 w-5",
            !lastPing.sentByAgent && "bg-transparent",
          )}
          className={cn("h-3 w-3", colorClass)}
          direction={
            isIncoming ? CallDirection.INCOMING : CallDirection.OUTGOING
          }
          hasTooltip={false}
          isAi={lastPing.sentByAgent ?? false}
          status={lastPing.status}
        />
      );
    }

    if (lastPing.type === PingType.EMAIL) {
      const color =
        colorClass ?? emailStatusIconColor(lastPing.status as EmailStatus);
      return (
        <Row className="items-center gap-0.5">
          {isIncoming ? (
            <ArrowDownLeftIcon className={cn("h-3 w-3 shrink-0", color)} />
          ) : (
            <ArrowUpRightIcon className={cn("h-3 w-3 shrink-0", color)} />
          )}
          <MailIcon className={cn("h-3 w-3 shrink-0", color)} />
        </Row>
      );
    }

    const color =
      colorClass ?? messageStatusIconColor(lastPing.status as QuoMessageStatus);
    return (
      <Row className="items-center gap-0.5">
        {isIncoming ? (
          <ArrowDownLeftIcon className={cn("h-3 w-3 shrink-0", color)} />
        ) : (
          <ArrowUpRightIcon className={cn("h-3 w-3 shrink-0", color)} />
        )}
        <MessageSquareIcon className={cn("h-3 w-3 shrink-0", color)} />
      </Row>
    );
  };

  return render();
};
